import ReduxStore from 'src/types/store/reduxStore';

import { AdvertisingMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';

import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';

import _get from 'lodash/get';
import {
  build4wComparisonRangeFilters,
  buildFullMonthRangeFilters,
  buildLast30DaysRangeFilters,
  buildLast60DaysRangeFilters,
  buildMtdRangeFilters,
  buildNextFullMonthRangeFilters
} from 'src/utils/dateformatting';
import { fetchAdvancedSearchData } from 'src/utils/Hooks/useGenericAdvancedSearch';
import { setPortfolioArray } from 'src/store/modules/adManager/adBudgetEditor/actions';

const cancelSignal = new AbortController();

const formatAllocationProfileList = (
  dateObj: { l30dRoas: any[]; mtdSpend: any[]; projectedSpend: any[]; activeSpend: any[] },
  arr2: any[]
) => {
  const result = arr2.map((entity) => {
    const entityData = dateObj?.l30dRoas?.find((data) => data.fieldId === entity.id);
    const mtdSpend = dateObj?.mtdSpend?.find((data) => data.fieldId === entity.id);
    const projectedSpend = dateObj?.projectedSpend?.find((data) => data.fieldId === entity.id);
    const activeSpend = dateObj?.activeSpend?.find((data) => data.fieldId === entity.id);

    return {
      ...entityData,
      isActive: activeSpend?.additionalValues?.spend_sum_value > 0,
      mtdSpend: mtdSpend?.additionalValues?.spend_sum_value || 0,
      projectedSpend: projectedSpend?.additionalValues?.spend_sum_value || 0,
      id: entity.id,
      entity
    };
  });

  result.sort((a: any, b: any) => {
    const aValue = _get(a, ['mtdSpend'], -1);
    const bValue = _get(b, ['mtdSpend'], -1);
    return bValue - aValue;
  });

  return result;
};

const getL30DRoasRequest = async (retailerId: string, keyMetricRequestBuilder: AdvancedSearchRequestBuilder) => {
  const last30DaysRangeFilters = buildLast30DaysRangeFilters();
  const last30DaysRangeFiltersComparison = await build4wComparisonRangeFilters();

  const aggregationBuilder = new AggregationBuilder('portfolioId');
  aggregationBuilder
    .addAggregationField('Ad Spend', AdvertisingMetric.AD_SPEND, 'sum', true)
    .addAggregationField('Ad Sales', AdvertisingMetric.AD_SALES, 'sum', true)
    .addAggregationField(
      'ROAS',
      AdvertisingMetric.ROAS,
      'computed',
      true,
      '((spend_sum > 0) ? ((sales_sum / spend_sum)) : (0))'
    )
    .addConditionRangeFilter('dayId', last30DaysRangeFilters[0].minValue, last30DaysRangeFilters[0].maxValue)
    .addConditionTermFilter('retailerId', [retailerId])
    .addComparisonRangeFilter(
      'dayId',
      last30DaysRangeFiltersComparison[0].minValue,
      last30DaysRangeFiltersComparison[0].maxValue
    );

  keyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionTermFilter('isProjected', 'must', [0])
    .addConditionRangeFilter('dayId', last30DaysRangeFilters[0].minValue, last30DaysRangeFilters[0].maxValue)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId');

  const keyMetricWeeklyRequest = keyMetricRequestBuilder.build();
  return keyMetricWeeklyRequest;
};

export const getMTDSpendRequest = (retailerId: string, keyMetricRequestBuilder: AdvancedSearchRequestBuilder) => {
  const mtdRangeFilters = buildMtdRangeFilters();

  const aggregationBuilder = new AggregationBuilder('portfolioId');
  aggregationBuilder
    .addAggregationField('Ad Spend', AdvertisingMetric.AD_SPEND, 'sum', true)

    .addConditionRangeFilter('dayId', mtdRangeFilters[0].minValue, mtdRangeFilters[0].maxValue)
    .addConditionTermFilter('retailerId', [retailerId]);

  keyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionTermFilter('isProjected', 'must', [0])
    .addConditionRangeFilter('dayId', mtdRangeFilters[0].minValue, mtdRangeFilters[0].maxValue)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())

    .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId');

  const keyMetricWeeklyRequest = keyMetricRequestBuilder.build();
  return keyMetricWeeklyRequest;
};

export const getProjectedSpendRequest = (
  retailerId: string,
  keyMetricRequestBuilder: AdvancedSearchRequestBuilder,
  timelineName: ReduxStore['adBudgetEditor']['timeline']['name'] = 'currentMonthBudgetSetting'
) => {
  const mtdRangeFilters =
    timelineName === 'currentMonthBudgetSetting' ? buildFullMonthRangeFilters() : buildNextFullMonthRangeFilters();

  const aggregationBuilder = new AggregationBuilder('portfolioId');
  aggregationBuilder
    .addAggregationField('Ad Spend', AdvertisingMetric.AD_SPEND, 'sum', true)
    .addConditionRangeFilter('dayId', mtdRangeFilters[0].minValue, mtdRangeFilters[0].maxValue)
    .addConditionTermFilter('retailerId', [retailerId]);

  keyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionRangeFilter('dayId', mtdRangeFilters[0].minValue, mtdRangeFilters[0].maxValue)
    .addConditionTermFilter('isProjected', 'must', [1])
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId');

  const keyMetricWeeklyRequest = keyMetricRequestBuilder.build();
  return keyMetricWeeklyRequest;
};

const getActiveRequest = (retailerId: string, keyMetricRequestBuilder: AdvancedSearchRequestBuilder) => {
  const mtdRangeFilters = buildLast60DaysRangeFilters();
  const aggregationBuilder = new AggregationBuilder('portfolioId');
  aggregationBuilder
    .addAggregationField('Ad Spend', AdvertisingMetric.AD_SPEND, 'sum', true)

    .addConditionRangeFilter('dayId', mtdRangeFilters[0].minValue, mtdRangeFilters[0].maxValue)
    .addConditionTermFilter('retailerId', [retailerId]);
  keyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionRangeFilter('dayId', mtdRangeFilters[0].minValue, mtdRangeFilters[0].maxValue)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())

    .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId');

  const keyMetricWeeklyRequest = keyMetricRequestBuilder.build();
  return keyMetricWeeklyRequest;
};

export const getAllocationMetric = () => async (dispatch: (action: any) => void, getState: () => ReduxStore) => {
  cancelSignal.abort();
  const requestEndpoint = `api/advertising/AdvancedSearch?_id=PortfolioAllocationMetrics`;
  const retailerId = getState().retailer.id;
  const { adPortfolios } = getState();

  const reachMetricId = 'l30d-roas-metrics';
  const l30dRoasRequestBuilder = new AdvancedSearchRequestBuilder(reachMetricId, 'advertising-adCampaignDailyMetrics');
  const l30dRoasRequest = await getL30DRoasRequest(retailerId, l30dRoasRequestBuilder);

  const mtdSpendMetricId = 'mtd-spend-metrics';
  const mtdSpendRequestBuilder = new AdvancedSearchRequestBuilder(
    mtdSpendMetricId,
    'advertising-adCampaignDailyMetrics'
  );
  const mtdSpendRequest = getMTDSpendRequest(retailerId, mtdSpendRequestBuilder);

  const projectedSpendMetricId = 'projected-spend-metrics';
  const projectedSpendRequestBuilder = new AdvancedSearchRequestBuilder(
    projectedSpendMetricId,
    'advertising-adCampaignDailyMetrics'
  );
  const projectedSpendRequest = getProjectedSpendRequest(
    retailerId,
    projectedSpendRequestBuilder,
    'currentMonthBudgetSetting'
  );

  const activeMetricId = 'active-metrics';
  const activeRequestBuilder = new AdvancedSearchRequestBuilder(activeMetricId, 'advertising-adCampaignDailyMetrics');
  const activeRequest = getActiveRequest(retailerId, activeRequestBuilder);

  const data = await fetchAdvancedSearchData({
    cancelTokenConfig: { signal: cancelSignal.signal },
    requestEndpoint,
    requestBody: [l30dRoasRequest, mtdSpendRequest, projectedSpendRequest, activeRequest]
  });

  const adPortfolioDailyMetrics = {
    l30dRoas: data ? data.data[0].aggregations.by_portfolioId : null,
    mtdSpend: data ? data.data[1].aggregations.by_portfolioId : null,
    projectedSpend: data ? data.data[2].aggregations.by_portfolioId : null,
    activeSpend: data ? data.data[3].aggregations.by_portfolioId : null
  };

  dispatch(setPortfolioArray(formatAllocationProfileList(adPortfolioDailyMetrics, adPortfolios)));
};
